import React from "react";
import PropTypes from "prop-types";
import { Chip, Tooltip } from "@mui/material";

/**
 * @param {Object} props
 * @returns {React.Component}
 */
const CustomChip = ({
  label,
  icon,
  color,
  size,
  variant,
  withTooltip,
  tooltipTitle,
  tooltipPlacement,
  backgroundColor,
  textColor,
  borderColor,
  customStyles,
  active,
  clickable,
  onDelete,
  deleteIcon,
  avatar,
  disabled,
  onClick,
  ...otherProps
}) => {
  const chipStyles = {
    backgroundColor:
      backgroundColor || (color !== "default" ? undefined : "#e0e0e0"),
    color: textColor || "#000",
    boxShadow: "none",
    height: size === "small" ? "16px" : "24px",
    fontSize: size === "small" ? "0.55rem" : "0.75rem",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
    paddingLeft: "4px",
    paddingRight: "4px",
    "& .MuiChip-label": {
      padding: "0 4px",
    },
    ...(active !== undefined && {
      opacity: active ? 1 : 0.6,
    }),
    ...(borderColor && {
      border: `1px solid ${borderColor}`,
    }),
    ...customStyles,
  };

  const iconStyles = {
    fontSize: size === "small" ? "13px" : "16px",
    color: textColor || (color !== "default" ? "#FFF" : "#000"),
    marginRight: "2px",
  };

  const labelStyles = {
    alignItems: "center",
    justifyContent: "center",
    color: textColor,
    lineHeight: "1",
    padding: "0 1px",
  };

  const chip = (
    <Chip
      label={
        typeof label === "string" ? (
          <span style={labelStyles}>{label.toUpperCase()}</span>
        ) : (
          label
        )
      }
      icon={icon && React.cloneElement(icon, { style: iconStyles })}
      size={size}
      variant={variant}
      color={color}
      clickable={clickable}
      onDelete={onDelete}
      deleteIcon={deleteIcon}
      avatar={avatar}
      disabled={disabled}
      onClick={onClick}
      sx={chipStyles}
      {...otherProps}
    />
  );

  return withTooltip ? (
    <Tooltip
      title={tooltipTitle || ""}
      placement={tooltipPlacement || "top"}
      arrow
    >
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

CustomChip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  icon: PropTypes.element,
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf(["filled", "outlined"]),
  withTooltip: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  tooltipPlacement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
  ]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  borderColor: PropTypes.string,
  customStyles: PropTypes.object,
  active: PropTypes.bool,
  clickable: PropTypes.bool,
  onDelete: PropTypes.func,
  deleteIcon: PropTypes.element,
  avatar: PropTypes.element,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

CustomChip.defaultProps = {
  label: "",
  color: "default",
  size: "medium",
  variant: "filled",
  withTooltip: false,
  active: true,
  clickable: false,
  disabled: false,
};

export default CustomChip;
